import { useModelContext } from '@app/contexts/model';
import { useStorageContext } from '@app/contexts/storage';
import { MAIN_PAGE } from '@app/enums';
import { useStorageConfirmTranslateContext } from '@app/modules/ConfirmTranslate/contexts/storage';
import { useStorageUserListContext } from '@app/modules/SelfConfig/contexts/storage';
import { useStorageTranslateContext } from '@app/modules/Translate/contexts/storage';
import { faCheckSquare, faLanguage, faSignOut, faUser, faUserCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconButton } from '@mui/material';
import classnames from 'classnames';
import { observer } from 'mobx-react';
import React, { FC } from 'react';
import './ModuleButtons.scss';

type TIconProps = {
    page: MAIN_PAGE;
};

const LogOutIcon: FC<TIconProps> = observer(function (props: TIconProps) {
    const { globalStorage } = useStorageContext();
    const { usersListStore } = useStorageUserListContext();
    const { translateStore } = useStorageTranslateContext();
    const { languageFileModel } = useModelContext();
    const { confirmTranslateStore } = useStorageConfirmTranslateContext();
    return (
        <FontAwesomeIcon
            icon={faSignOut}
            className={classnames(' moduleButtons__i moduleButtons__i--margin moduleButtons__i--logout', {
                'moduleButtons__i--selected': globalStorage.openModule === props.page
            })}
            title='Log out'
            aria-hidden='true'
            onClick={() => {
                globalStorage.LogOutIconClicked = true;
                globalStorage.ConfirmSelectDiffTranslateIconClicked = false;
                globalStorage.SelfConfIconClicked = false;
                globalStorage.SelectDiffTranslateIconClicked = false;
                if (translateStore.changeSet.size != 0) {
                    globalStorage.openModule = MAIN_PAGE.TRANSLATE;
                    translateStore.handleOpenAlert();
                } else {
                    globalStorage.openModule = props.page;
                    usersListStore.addUser = false;
                    translateStore.clear();
                    languageFileModel.dataLanguage.set(null);
                    confirmTranslateStore.clear();
                }
            }}
        />
    );
});
const TranslateIcon: FC<TIconProps> = observer(function (props: TIconProps) {
    const { globalStorage } = useStorageContext();
    const { usersListStore } = useStorageUserListContext();
    const { confirmTranslateStore } = useStorageConfirmTranslateContext();
    const { languageFileModel } = useModelContext();
    const { translateStore } = useStorageTranslateContext();

    return (
        <IconButton
            disabled={globalStorage.openModule === MAIN_PAGE.TRANSLATE}
            title='Proceed to translation site'
            aria-hidden='true'
            onClick={() => {
                globalStorage.ConfirmSelectDiffTranslateIconClicked = false;
                globalStorage.SelfConfIconClicked = false;
                globalStorage.LogOutIconClicked = false;
                globalStorage.openModule = props.page;
                usersListStore.addUser = false;
                confirmTranslateStore.clear();
                translateStore.clear();
                languageFileModel.dataLanguage.set(null);
            }}>
            <FontAwesomeIcon
                className={classnames('moduleButtons__i moduleButtons__i--translate moduleButtons__i--margin', {
                    'moduleButtons__i--selected': globalStorage.openModule === props.page
                })}
                icon={faLanguage}
            />
        </IconButton>
    );
});

const ConfirmTranslateIcon: FC<TIconProps> = observer(function (props: TIconProps) {
    const { globalStorage } = useStorageContext();
    const { usersListStore } = useStorageUserListContext();
    const { langFilesAcceptedModel } = useModelContext();
    const { confirmTranslateStore } = useStorageConfirmTranslateContext();
    const { translateStore } = useStorageTranslateContext();
    const { languageFileModel } = useModelContext();

    return (
        <FontAwesomeIcon
            icon={faCheckSquare}
            className={classnames('moduleButtons__i moduleButtons__i--margin', {
                'moduleButtons__i--selected': globalStorage.openModule === props.page
            })}
            title='List of translations to confirm'
            aria-hidden='true'
            onClick={() => {
                globalStorage.ConfirmSelectDiffTranslateIconClicked = true;
                globalStorage.SelfConfIconClicked = false;
                globalStorage.LogOutIconClicked = false;
                globalStorage.SelectDiffTranslateIconClicked = false;
                if (translateStore.changeSet.size != 0) {
                    globalStorage.openModule = MAIN_PAGE.TRANSLATE;
                    translateStore.handleOpenAlert();
                } else {
                    langFilesAcceptedModel.findFiles().catch((reason) => null);
                    globalStorage.openModule = props.page;
                    usersListStore.addUser = false;
                    translateStore.changeSet.clear();
                    languageFileModel.dataLanguage.set(null);
                    confirmTranslateStore.clear();
                    translateStore.clear();
                }
            }}
        />
    );
});

const SelfConfIcon: FC<TIconProps> = observer(function (props: TIconProps) {
    const { globalStorage } = useStorageContext();
    const { usersModel } = useModelContext();
    const { usersListStore } = useStorageUserListContext();
    const { confirmTranslateStore } = useStorageConfirmTranslateContext();
    const { languageFileModel } = useModelContext();
    const { translateStore } = useStorageTranslateContext();

    return (
        <FontAwesomeIcon
            icon={faUser}
            className={classnames('moduleButtons__i moduleButtons__i--margin', {
                'moduleButtons__i--selected': globalStorage.openModule === props.page
            })}
            title='Users list'
            aria-hidden='true'
            onClick={() => {
                globalStorage.ConfirmSelectDiffTranslateIconClicked = false;
                globalStorage.SelfConfIconClicked = true;
                globalStorage.LogOutIconClicked = false;
                globalStorage.SelectDiffTranslateIconClicked = false;
                if (translateStore.changeSet.size != 0) {
                    globalStorage.openModule = MAIN_PAGE.TRANSLATE;
                    translateStore.handleOpenAlert();
                } else {
                    usersModel.findUser().catch((reason) => console.error(reason));
                    globalStorage.openModule = props.page;
                    usersListStore.addUser = false;
                    languageFileModel.dataLanguage.set(null);
                    translateStore.changeSet.clear();
                    confirmTranslateStore.clear();
                    translateStore.clear();
                }
            }}
        />
    );
});

const UserConfIcon: FC<TIconProps> = observer(function (props: TIconProps) {
    return null;
});

const Icons: { [key in MAIN_PAGE]: FC<TIconProps> } = {
    [MAIN_PAGE.LOGOUT]: LogOutIcon,
    [MAIN_PAGE.TRANSLATE]: TranslateIcon,
    [MAIN_PAGE.CONFIRM_TRANSLATE]: ConfirmTranslateIcon,
    [MAIN_PAGE.SELF_CONFIG]: SelfConfIcon,
    [MAIN_PAGE.USER_CONFIG]: UserConfIcon
};

type TProps = {};
export const ModuleButtons: FC<TProps> = function (props: TProps) {
    const { authorizationModel } = useModelContext();
    const { globalStorage } = useStorageContext();
    let user = authorizationModel.user.get();

    const I =
        user &&
        user.modules.map((value) => {
            const Ico = Icons[value];
            if (!globalStorage.LogOutIconClicked) {
                return <Ico key={value} page={value} />;
            }
        });

    return (
        <div className={'moduleButtons'}>
            {I || <FontAwesomeIcon icon={faUserCircle} className='moduleButtons__i--margin' aria-hidden='true' />}
            {globalStorage.LogOutIconClicked ? <FontAwesomeIcon icon={faUserCircle} className='moduleButtons__i--margin' aria-hidden='true' /> : <></>}
        </div>
    );
};
